/**
 * Feature flags configuration
 * Centralized management of feature toggles for the application
 */

export interface FeatureFlags {
  approvalFlow: boolean;
  approvalWithContext: boolean;
  invoiceUpload: boolean;
  notifications: boolean;
  moneyTransfer: boolean;
  settings: boolean;
  userPermissions: boolean;
  inAppPayments: boolean;
  erpConnection: boolean;
  socialLogin: boolean;
}

/**
 * Get feature flags based on environment variables
 */
export function getFeatureFlags(): FeatureFlags {
  // Default to enabled in development, disabled in production
  const isDev = import.meta.env.DEV;
  const approvalFlowEnv = import.meta.env.VITE_ENABLE_APPROVAL_FLOW;
  const approvalWithContextEnv = import.meta.env.VITE_ENABLE_APPROVAL_WITH_CONTEXT;
  const invoiceUploadEnv = import.meta.env.VITE_ENABLE_INVOICE_UPLOAD;
  const notificationsEnv = import.meta.env.VITE_ENABLE_NOTIFICATIONS;
  const moneyTransferEnv = import.meta.env.VITE_ENABLE_MONEY_TRANSFER;
  const settingsEnv = import.meta.env.VITE_ENABLE_SETTINGS;
  const userPermissionsEnv = import.meta.env.VITE_ENABLE_USER_PERMISSIONS;
  const inAppPaymentsEnv = import.meta.env.VITE_ENABLE_IN_APP_PAYMENTS;
  const erpConnectionEnv = import.meta.env.VITE_ENABLE_ERP_CONNECTION;
  const socialLoginEnv = import.meta.env.VITE_ENABLE_SOCIAL_LOGIN;

  // Parse environment variable with fallback to dev mode default
  // Handle both string values and actual booleans
  let approvalFlow: boolean;
  if (approvalFlowEnv !== undefined) {
    approvalFlow = approvalFlowEnv === 'true' || approvalFlowEnv === true;
  } else {
    approvalFlow = isDev;
  }

  let invoiceUpload: boolean;
  if (invoiceUploadEnv !== undefined) {
    invoiceUpload = invoiceUploadEnv === 'true' || invoiceUploadEnv === true;
  } else {
    invoiceUpload = isDev;
  }

  let notifications: boolean;
  if (notificationsEnv !== undefined) {
    notifications = notificationsEnv === 'true' || notificationsEnv === true;
  } else {
    notifications = false; // Default to disabled (off)
  }

  let moneyTransfer: boolean;
  if (moneyTransferEnv !== undefined) {
    moneyTransfer = moneyTransferEnv === 'true' || moneyTransferEnv === true;
  } else {
    moneyTransfer = isDev;
  }

  let settings: boolean;
  if (settingsEnv !== undefined) {
    settings = settingsEnv === 'true' || settingsEnv === true;
  } else {
    settings = false; // Default to disabled (off)
  }

  let userPermissions: boolean;
  if (userPermissionsEnv !== undefined) {
    userPermissions = userPermissionsEnv === 'true' || userPermissionsEnv === true;
  } else {
    userPermissions = false; // Default to disabled (off)
  }

  let approvalWithContext: boolean;
  if (approvalWithContextEnv !== undefined) {
    approvalWithContext = approvalWithContextEnv === 'true' || approvalWithContextEnv === true;
  } else {
    approvalWithContext = true; // Default to enabled for model training
  }

  let inAppPayments: boolean;
  if (inAppPaymentsEnv !== undefined) {
    inAppPayments = inAppPaymentsEnv === 'true' || inAppPaymentsEnv === true;
  } else {
    inAppPayments = false; // Default to disabled until payment flow is fully tested
  }

  let erpConnection: boolean;
  if (erpConnectionEnv !== undefined) {
    erpConnection = erpConnectionEnv === 'true' || erpConnectionEnv === true;
  } else {
    erpConnection = false; // Default to disabled (off)
  }

  let socialLogin: boolean;
  if (socialLoginEnv !== undefined) {
    socialLogin = socialLoginEnv === 'true' || socialLoginEnv === true;
  } else {
    socialLogin = false; // Default to disabled — Google/Azure OAuth providers and manual-linking are not configured consistently across environments
  }

  return {
    approvalFlow,
    approvalWithContext,
    invoiceUpload,
    notifications,
    moneyTransfer,
    settings,
    userPermissions,
    inAppPayments,
    erpConnection,
    socialLogin,
  };
}

/**
 * Utility function to check if approval flow is enabled
 * Always gets fresh feature flags to handle environment changes
 */
export const isApprovalFlowEnabled = (): boolean => {
  return getFeatureFlags().approvalFlow;
};

/**
 * Utility function to check if invoice upload is enabled
 * Always gets fresh feature flags to handle environment changes
 */
export const isInvoiceUploadEnabled = (): boolean => {
  return getFeatureFlags().invoiceUpload;
};

/**
 * Utility function to check if notifications are enabled
 * Always gets fresh feature flags to handle environment changes
 */
export const isNotificationsEnabled = (): boolean => {
  return getFeatureFlags().notifications;
};

/**
 * Utility function to check if money transfer is enabled
 * Always gets fresh feature flags to handle environment changes
 */
export const isMoneyTransferEnabled = (): boolean => {
  return getFeatureFlags().moneyTransfer;
};

/**
 * Utility function to check if settings are enabled
 * Always gets fresh feature flags to handle environment changes
 */
export const isSettingsEnabled = (): boolean => {
  return getFeatureFlags().settings;
};

/**
 * Utility function to check if user permissions are enabled
 * Always gets fresh feature flags to handle environment changes
 */
export const isUserPermissionsEnabled = (): boolean => {
  return getFeatureFlags().userPermissions;
};

/**
 * Utility function to check if approval with context is enabled
 * Always gets fresh feature flags to handle environment changes
 */
export const isApprovalWithContextEnabled = (): boolean => {
  return getFeatureFlags().approvalWithContext;
};

/**
 * Utility function to check if in-app payment initiation is enabled.
 * When false, the payment flow is gated and users see a modal directing
 * them to pay via their own bank.
 */
export const isInAppPaymentsEnabled = (): boolean => {
  return getFeatureFlags().inAppPayments;
};

/**
 * Utility function to check if ERP connection (Business Central) is enabled
 * Always gets fresh feature flags to handle environment changes
 */
export const isErpConnectionEnabled = (): boolean => {
  return getFeatureFlags().erpConnection;
};

/**
 * Utility function to check if social login (Google/Microsoft OAuth) is enabled
 * Always gets fresh feature flags to handle environment changes
 */
export const isSocialLoginEnabled = (): boolean => {
  return getFeatureFlags().socialLogin;
};